// Phase 6 — Inline metadata edit dialog for terrain + obstacles.
//
// The DEM bytes (and the GeoJSON for obstacles) are immutable. This
// dialog only edits the metadata wrapper: name, description, tags.
// The server returns 409 with a canonical "Preset is immutable;
// clone first." message on preset rows for non-admin operators —
// the parent page surfaces that as a toast.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import SpinnerButton from "@/components/SpinnerButton";

export default function EntityMetadataDialog({
    open,
    onOpenChange,
    entity,
    entityType = "asset",
    onSave,
    testId,
}) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [tagsRaw, setTagsRaw] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && entity) {
            setName(entity.name || "");
            setDescription(entity.description || "");
            setTagsRaw((entity.tags || []).join(", "));
            setBusy(false);
        }
    }, [open, entity]);

    const onSubmit = async () => {
        setBusy(true);
        try {
            const tags = tagsRaw
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
            await onSave?.({
                name: name.trim(),
                description: description,
                tags,
            });
            onOpenChange?.(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={testId || `${entityType}-metadata-dialog`}>
                <DialogHeader>
                    <DialogTitle>Edit {entityType} metadata</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label
                            htmlFor="md-name"
                            className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3"
                        >
                            Name
                        </Label>
                        <Input
                            id="md-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="h-8 text-[12px] font-mono"
                            data-testid="md-name"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label
                            htmlFor="md-description"
                            className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3"
                        >
                            Description
                        </Label>
                        <Textarea
                            id="md-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="text-[12px] font-mono"
                            data-testid="md-description"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label
                            htmlFor="md-tags"
                            className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3"
                        >
                            Tags (comma separated)
                        </Label>
                        <Input
                            id="md-tags"
                            value={tagsRaw}
                            onChange={(e) => setTagsRaw(e.target.value)}
                            placeholder="e.g. urban, low-altitude"
                            className="h-8 text-[12px] font-mono"
                            data-testid="md-tags"
                        />
                    </div>
                    <p className="text-[11px] font-mono text-ux-text-3 italic">
                        Note: the underlying {entityType === "terrain" ? "DEM raster" : "GeoJSON"}
                        {" "}is immutable. Only metadata is editable here. Use Clone to fork.
                    </p>
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange?.(false)}
                        data-testid="md-cancel"
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <SpinnerButton
                        size="sm"
                        loading={busy}
                        onClick={onSubmit}
                        testId="md-save"
                    >
                        Save metadata
                    </SpinnerButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
